/**This file holds context for mapservices and related information
 * can be used across the application 
 */
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { useUserGroups } from './common/Auth/UserGroupsContext';
import fetchDataWithAuth from '../utils/api';
import { useTranslation } from 'react-i18next';
import { getLocalStorageItem } from '../utils/HelperFunctions';
import { useNavigate } from "react-router-dom";
import esriId from '@arcgis/core/identity/IdentityManager';
import { useLocation } from "react-router-dom";

const MapServiceContext = createContext();

export const MapServiceProvider = ({ children }) => {
    const { groupServices } = useUserGroups();
    const [loading, showLoading] = useState(false);
    const [mapServicesFetched, setMapServicesFetched] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [publicMapServices, setPublicMapServices] = useState([]);
    const [userMapServices, setUserMapServices] = useState([]);
    const [allMapServices, setAllMapServices] = useState([]);
    const [mapServices, setMapServices] = useState([]);
    const [mapServiceInfo, setMapserviceInfo] = useState({});
    const location = useLocation();
    const [webView, setWebView] = useState(null);

    useEffect(() => {
        const userAgent = navigator.userAgent || "";
        const _isWebView = userAgent.includes("EDPFlutterApp");
        setWebView(_isWebView);
        localStorage.setItem("webView", _isWebView);
    }, []);

    const getMapservices = useCallback(async () => {
        try {
            // console.log('fetching map services........');
            showLoading(true);
            let sourceLayers = [];
            /** Get map services through API */
            const userID = getLocalStorageItem("UserId");
            if (userID?.trim()) {
                let response = await fetchDataWithAuth(
                    `/PortalConfig/userMapServices/${userID}`,
                    "GET",
                    navigate,
                    t
                );
                if (response.length) {
                    sourceLayers.push(...response);
                }
            }

            let response = await fetch(
                `${process.env.REACT_APP_API_URL}/PortalConfig/publicMapServices`, {
                method: "GET",
            }
            );
            if (!response.ok) {
                const errorText = await response.json();
                throw new Error(`${response.status}: ${errorText.message}`);
            }
            const _sourceLayers = await response.json();
            sourceLayers.push(..._sourceLayers);

            // Filter unique layers based on mapServiceURL
            const uniqueLayers = sourceLayers.filter((layer, index, self) =>
                index === self.findIndex((l) => l.mapServiceURL === layer.mapServiceURL)
            );

            setMapServices(uniqueLayers);
            setMapServicesFetched(true);

        } catch (error) {
            showLoading(false);
        } finally {
            showLoading(false);
        }
    }, []);

    const getAllMapservices = useCallback(async () => {
        try {
            showLoading(true);
            let sourceLayers = [];

            let response = await fetch(
                `${process.env.REACT_APP_API_URL}/PortalConfig/allMapServices`, {
                method: "GET",
            }
            );
            if (!response.ok) {
                const errorText = await response.json();
                throw new Error(`${response.status}: ${errorText.message}`);
            }
            const _sourceLayers = await response.json();
            sourceLayers.push(..._sourceLayers);

            // Filter unique layers based on mapServiceURL
            const uniqueLayers = sourceLayers.filter((layer, index, self) =>
                index === self.findIndex((l) => l.mapServiceURL === layer.mapServiceURL)
            );

            setMapServices(uniqueLayers);
            setMapServicesFetched(true);

        } catch (error) {
            showLoading(false);
        } finally {
            showLoading(false);
        }
    }, []);

    // useEffect(() => {
    //     if (!groupServices || !groupServices.length) return;
    //     const fetchMapServices = async () => {
    //         await getMapservices();
    //     }
    //     fetchMapServices();

    //     console.log('groupServices', groupServices);
    //     console.log('allMapServices', allMapServices);
    // }, [groupServices]);

    useEffect(() => {
        if (webView === null) return;

        if (location.pathname === "/map" || location.pathname === "/") {
            const fetchMapServices = async () => {
                if (webView) {
                    await getAllMapservices();
                } else {
                    await getMapservices();
                }
            };
            fetchMapServices();
        }
    }, [location.pathname, webView]);

    useEffect(() => {
        if (!mapServices || !mapServices.length > 0) return;

        let _mapServiceIds = mapServices.map((mapService) => Number(mapService.id)).filter(id => !isNaN(id));
        if (_mapServiceIds.length) {
            fetchMapServiceDetails(_mapServiceIds);
        }
        const userID = getLocalStorageItem("UserId");
        if (userID) {
            authenticateSecuredServices();/**Identity Manager using single token for all secured services */
        }


    }, [mapServices]);

    const fetchMapServiceDetails = async (mapServiceIds) => {
        try {
            // const data = await fetchDataWithAuth(
            //     `/PortalConfig/mapServices/info`,
            //     "POST",
            //     navigate,
            //     t,
            //     mapServiceIds
            // );
            const response = await fetch(`${process.env.REACT_APP_API_URL}/PortalConfig/mapServices/info`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(mapServiceIds),
            });

            if (!response.ok) {
                const errorText = await response.json();
                throw new Error(`${response.status}: ${errorText.message}`);
            }

            const data = await response.json();
            setMapserviceInfo(data);
        } catch (error) {
            console.log('failed to get mapservices info', error.message);
        }
    };

    const authenticateSecuredServices = () => {
        try {
            const securedServices = mapServices.filter(service => service.isSecured);
            if (securedServices.length > 0) {
                // console.log('authenticating secured services...');
                // Extract unique server URLs
                const uniqueServers = [...new Set(
                    // securedServices.map(service => `${service.mapServiceURL.split("/services/")[0]}/services`)
                    securedServices.map(service => `${service.mapServiceURL}`)
                )];

                uniqueServers.forEach(server => {
                    const existingCredential = esriId.findCredential(server);
                    const currentTime = Date.now() / 1000; // Convert to seconds

                    // console.log('existingCredential', existingCredential);
                    // Check if token exists and is still valid
                    if (existingCredential?.token && (existingCredential.expires) > currentTime) {
                        // console.log(`Token still valid for server: ${server}`);
                        return; // Skip re-registration
                    }

                    // Get the token from the first matching secured service
                    const _securedService = securedServices.find(service =>
                        service.mapServiceURL.startsWith(server)
                    );
                    const token = _securedService?.token;
                    const tokenExpiryDate = _securedService?.tokenExpirationDate;
                    const expires = new Date(tokenExpiryDate).getTime(); // Convert to milliseconds
                    const timeNow = new Date().getTime();
                    //console.log(token)
                    if (timeNow > expires) {
                        //console.log(`Token expired for mapservice: ${_securedService.enMapServiceName}`);
                        return;
                    }
                    if (token) {
                        // console.log(`Registering or updating token ${token} with validity ${tokenExpiryDate} for: ${server}`);
                        esriId.registerToken({
                            expires,
                            server,
                            token
                        });
                    } else {
                       // console.log(`No token found for secured service`, _securedService.enMapServiceName);
                    }
                });
            }
        } catch (error) {
          //  console.log("Error registering token:", error);
        }
    };

    const registerMapServiceToken = (serviceUrl, token) => {
        try {
            if (!serviceUrl || !token) {
                // console.log('Invalid service URL or token');
                return;
            }
            // console.log(`Registering token for server: ${serviceUrl}`);
            esriId.registerToken({
                server: serviceUrl,
                token
            });
        }
        catch (error) {
            //console.log('Error registering token:', error);
        }
    };

    return (
        <MapServiceContext.Provider value={{
            publicMapServices,
            userMapServices,
            mapServices,
            mapServiceInfo,
            registerMapServiceToken
        }}>
            {children}
        </MapServiceContext.Provider>
    )
};

export const useMapServices = () => useContext(MapServiceContext);